<template>
    <page :busy="busy" class="merge-guest-page">
        <grid :rowData="items" :columnDefs="columnDefs" class="pt-3">
            <div class="ml-2 different-email-description">
                <span>
                    Guests listed below have two guest records with the same email address and should be merged (unless
                    present in merge exceptions).<br />
                </span>
                <span>
                    To merge two guests with different email addresses, please click
                    <b-button variant="default" class="show-modal-button" @click="showMergeModal">HERE</b-button><br />
                </span>
                <span>
                    To add guests to the online reservations guest merge exception list, please click
                    <b-button variant="default" class="show-modal-button" @click="showExceptionsModal">HERE</b-button>
                </span>
            </div>
        </grid>
        <validation-observer ref="merge_form" v-slot="{ meta, errors, validate }">
            <modal
                id="merge-modal"
                title="Merge Guests"
                ok-title="Merge"
                @ok.prevent="mergeGuestsWithDifferentEmails"
                :ok-disabled="disableModal || !meta.valid || busy"
                @hidden="onHideMergeModal"
                @shown="validate()"
            >
                <div class="merge-source-wrapper">
                    <validation-provider name="source" rules="required" >
                        <form-input
                            id="source"
                            @input="(value) => searchGuests(value, true)"
                            label="Source:"
                            :value="sourceValue"
                            placeholder="Source Guest Email"
                            :error="errors.source"
                        />
                    </validation-provider>
                    <div class="merge-guests-wrapper" v-if="sourceGuestsList.length && sourceValue">
                        <div
                            class="guest-item p-2"
                            :key="guest.id"
                            v-for="guest in sourceGuestsList"
                            @click="selectSource(guest)"
                        >
                            <span>{{ guest.email }}</span>
                        </div>
                    </div>
                </div>
                <div class="merge-source-wrapper">
                    <validation-provider name="target" rules="required" >
                        <form-input
                            id="target"
                            @input="(value) => searchGuests(value, false)"
                            label="Target:"
                            placeholder="Target Guest Email"
                            :value="targetValue"
                            :error="errors.target"
                        />
                    </validation-provider>
                    <span>{{ duplicateText }}</span>
                    <div class="merge-guests-wrapper" v-if="targetGuestsList.length && targetValue">
                        <div
                            class="guest-item p-2"
                            :key="guest.id"
                            v-for="guest in targetGuestsList"
                            @click="selectTarget(guest)"
                        >
                            <span>{{ guest.email }}</span>
                        </div>
                    </div>
                </div>
                <span class="d-flex mt-2 small text-danger"
                    >Warning: Proceed with caution. Guest data and reservations from source guest will be assigned to
                    target guest. This operation cannot be undone.</span
                >
            </modal>
        </validation-observer>

        <modal :id="exceptionsModalId" title="Merge Guests Exceptions" ok-title="OK" size="lg">
            <div class="modal-wrapper">
                <grid
                    :columnDefs="exceptionsColumnDefs"
                    :fetchCallback="exceptionsFetchCallback"
                    :isSearch="false"
                    ref="grid"
                >
                </grid>
            </div>

            <template #modal-footer>
                <b-button variant="info" class="px-3" @click="showAddEditMergeExceptionModal(false)"
                    >Add Exception</b-button
                >
                <b-button variant="primary" class="px-3" @click="hideExceptionsModal">OK</b-button>
            </template>
        </modal>
        <validation-observer ref="exception_form" v-slot="{ handleSubmit, meta }">
            <modal
                :id="addEditMergeExceptionModalId"
                title="Add Exception"
                ok-title="OK"
                :ok-disabled="!meta.valid"
                @ok.prevent="handleSubmit(_, addEditMergeException)"
                @hide="hideAddEditMergeExceptionModal"
                @shown="setExceptionData"
            >
                <b-form @submit.prevent="handleSubmit(_, addEditMergeException)">
                    <b-row>
                        <b-col>
                            <validation-provider name="email" rules="required|email" v-slot="{ errors, handleChange }">
                                <form-input
                                    floating-label="Email"
                                    v-model="currentException.email"
                                    :error="errors[0]"
                                    @input="handleChange"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                </b-form>
            </modal>
        </validation-observer>
    </page>
</template>

<script>
import API from '../../../api';
import Grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import {
    GUEST_MERGE_LIST_ENDPOINT,
    GUEST_MERGE_DIFFERENT_EMAILS_ENDPOINT,
    GUEST_MERGE_SAME_EMAILS_ENDPOINT,
    GUEST_ADD_MERGE_EXCEPTION_ENDPOINT,
    GUEST_UPDATE_MERGE_EXCEPTION_ENDPOINT,
    GUEST_DELETE_MERGE_EXCEPTION_ENDPOINT,
} from '../../../endpoints';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import Modal from '../../generic/modal/modal.vue';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import ActionsCellRendererExceptions from './actions-cell-renderer-exceptions.vue';

export default {
    components: {
        page,
        Grid,
        Modal,
        SearchMultiselect,
        FormInput,
        actionsCellRenderer: ActionsCellRenderer,
        actionsCellRendererExceptions: ActionsCellRendererExceptions,
    },
    data() {
        return {
            items: [],
            busy: null,
            targetGuestsList: [],
            sourceGuestsList: [],
            sourceValue: '',
            targetValue: '',
            selectedSource: null,
            selectedTarget: null,
            currentException: {},
            addEditMergeExceptionModalId: 'add-edit-exception-modal',
            exceptionsModalId: 'exceptions-modal',
            isExceptionUpdate: false,
        };
    },
    mounted() {
        this.fetchData();
    },
    mixins: [removeScrollMixin],
    computed: {
        disableModal() {
            return (
                this.selectedSource?.email !== this.sourceValue ||
                this.selectedTarget?.email !== this.targetValue ||
                this.selectedSource?.id === this.selectedTarget?.id
            );
        },
        columnDefs() {
            return [
                {
                    field: 'email',
                    headerName: 'Guest Email',
                    sort: 'asc',
                    minWidth: 200,
                },
                {
                    field: 'count',
                    headerName: 'Duplicates',
                },
                {
                    field: '',
                    cellRenderer: 'actionsCellRenderer',
                    minWidth: 140,
                    maxWidth: 140,
                    cellRendererParams: {
                        mergeGuest: async (email) => {
                            try {
                                await API.post(GUEST_MERGE_SAME_EMAILS_ENDPOINT(), {
                                    email,
                                });
                                await this.fetchData();
                                this.successToast({
                                    text: 'Guests successfully merged',
                                });
                            } catch (e) {
                                this.errorToast({
                                    text: 'Could not merge guests',
                                });
                            }
                        },
                    },
                },
            ];
        },
        exceptionsColumnDefs() {
            return [
                {
                    field: 'created',
                    headerName: 'Created',
                    maxWidth: 120,
                    type: 'dateColumn',
                    sort: 'desc',
                },
                {
                    field: 'email',
                    headerName: 'Email',
                    flex: 1,
                },
                {
                    headerName: '',
                    maxWidth: 120,
                    minWidth: 120,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRendererExceptions',
                    cellRendererParams: {
                        update: (exception) => {
                            this.currentException = exception;
                            this.showAddEditMergeExceptionModal(true);
                        },
                        delete: async (exception) => {
                            try {
                                await API.delete(GUEST_DELETE_MERGE_EXCEPTION_ENDPOINT(exception.id));
                            } catch (error) {
                                this.errorToast({
                                    text: `Could not delete merge exception.`,
                                });
                                return;
                            }
                            this.refresh();
                            this.fetchData();
                        },
                    },
                },
            ];
        },
        exceptionsFetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;

                const response = API.get(`/app/guest/merge-data/exceptions/grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });

                return response;
            };
        },
        duplicateText() {
            if (this.selectedSource && this.selectedTarget) {
                return this.selectedSource.id === this.selectedTarget.id
                    ? 'Source and target guests cannot be the same.'
                    : '';
            } else {
                return '';
            }
        },
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const response = await API.get(GUEST_MERGE_LIST_ENDPOINT());
                this.items = response.data.map((item) => ({
                    ...item,
                    id: (Math.random() * 1e128).toString(36),
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not load data' });
            } finally {
                this.busy = false;
            }
        },
        async mergeGuestsWithDifferentEmails() {
            try {
                this.busy = true
                await API.post(GUEST_MERGE_DIFFERENT_EMAILS_ENDPOINT(), {
                    source_guest_id: this.selectedSource.id,
                    target_guest_id: this.selectedTarget.id,
                });
                this.$bvModal.hide('merge-modal');
                this.successToast({ text: 'Guests successfully merged' });
            } catch (e) {
                this.errorToast({ text: 'Could not merge guests' });
            } finally {
                this.busy = false
            }
        },
        async addEditMergeException() {
            if (this.isExceptionUpdate) {
                this.updateMergeException();
            } else {
                this.addMergeException();
            }
        },
        async addMergeException() {
            try {
                this.busy = true;
                await API.post(GUEST_ADD_MERGE_EXCEPTION_ENDPOINT(), {
                    email: this.currentException.email,
                });
                this.hideAddEditMergeExceptionModal();
                this.successToast({ text: 'Merge exception successfully added' });
                this.refresh();
                this.fetchData();
            } catch (e) {
                this.errorToast({ text: 'Could not add merge exception' });
            } finally {
                this.busy = false;
            }
        },
        async updateMergeException() {
            try {
                this.busy = true;
                await API.patch(GUEST_UPDATE_MERGE_EXCEPTION_ENDPOINT(this.currentException.id), {
                    email: this.currentException.email,
                });
                this.hideAddEditMergeExceptionModal();
                this.successToast({ text: 'Merge exception successfully updated' });
                this.refresh();
                this.fetchData();
            } catch (e) {
                this.errorToast({ text: 'Could not update merge exception' });
            } finally {
                this.busy = false;
            }
        },
        selectSource(source) {
            this.selectedSource = source;
            this.sourceValue = source.email;
            this.sourceGuestsList = [];
            this.$refs.merge_form.setFieldValue('source', source.email);
        },
        selectTarget(source) {
            this.selectedTarget = source;
            this.targetValue = source.email;
            this.targetGuestsList = [];
            this.$refs.merge_form.setFieldValue('target', source.email);
        },
        onHideMergeModal() {
            this.selectedTarget = null;
            this.selectedSource = null;
            this.sourceValue = null;
            this.targetValue = null;
        },
        async searchGuests(value, isSource) {
            isSource ? (this.sourceValue = value) : (this.targetValue = value);
            try {
                const response = await API.get(`/app/guest/merge-data/search/?email=${value}`);
                isSource ? (this.sourceGuestsList = response.data) : (this.targetGuestsList = response.data);
            } catch (e) {
                this.errorToast({ text: 'Could not load guest list' });
            }
        },
        showMergeModal() {
            this.$bvModal.show('merge-modal');
        },
        showExceptionsModal() {
            this.$bvModal.show(this.exceptionsModalId);
        },
        hideExceptionsModal() {
            this.$bvModal.hide(this.exceptionsModalId);
        },
        showAddEditMergeExceptionModal(isExceptionUpdate) {
            if (!isExceptionUpdate) {
                this.currentException = {};
            }
            this.isExceptionUpdate = isExceptionUpdate;
            this.$bvModal.show(this.addEditMergeExceptionModalId);
        },
        hideAddEditMergeExceptionModal() {
            this.$bvModal.hide(this.addEditMergeExceptionModalId);
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        setExceptionData() {
            this.$refs.exception_form.setValues({
                email: this.currentException.email,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.merge-guest-page {
    .different-email-description {
        font-size: 13px;
    }
    .show-modal-button {
        font-size: 13px;
        padding: 0 4px;
    }
}
</style>

<style lang="scss">
$selected-button-color: #08c;
$primary-color: #0d47a1;
$scroll-background-color: #f5f5f5;
$border-color: #d8d8d8;
$font-color: #fff;

@mixin scroll-bar() {
    scrollbar-color: $primary-color $scroll-background-color;

    &::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px $border-color;
        border-radius: 10px;
        background-color: $scroll-background-color;
        border-right-width: 1px;
        border-left-width: 1px;
    }

    &::-webkit-scrollbar {
        width: 8px;
        height: 6px;
        background-color: $scroll-background-color;
        border-right-width: 1px;
        border-left-width: 1px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 10px;
        background-color: $primary-color;
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    }
}
.merge-source-wrapper {
    position: relative;
    .merge-guests-wrapper {
        padding-bottom: 10px;
        background: #fff;
        position: absolute;
        max-height: 35vh;
        width: 100%;
        overflow-y: auto;
        z-index: 9999;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 4px;
        scrollbar-width: thin;
        @include scroll-bar;
        .guest-item {
            cursor: pointer;
            &:hover {
                background: #0d47a1;
                color: #fff !important;
                span {
                    color: #fff !important;
                }
            }
        }
    }
}
.modal-wrapper {
    height: 300px;
}
</style>
